import React, {ChangeEvent, useMemo, useState} from 'react';
import InfoIcon from "../components/info-icon";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faFileArrowUp} from "@fortawesome/free-solid-svg-icons";
import {getRandomDashboardHeroImage} from "../helpers/dashboardHeroImages";
import * as localforage from "localforage";
import {loadDataFile, parseData} from "ebird-mydata-reader/dist/cjs/ebird-mydata-reader";
import {
    clearDerivedBirdDataCaches,
    LOCATION_ECO_DATA_CACHE_KEY,
    LOCATION_ECO_DATA_CACHE_VERSION,
    LOCATION_REGION_DATA_CACHE_KEY,
    LOCATION_REGION_DATA_CACHE_VERSION
} from "../helpers/birdDataCache";
import {
    buildObservationDatasetSignature,
    compactObservationData,
    getYearsFromStoredBirdData,
    hydrateStoredObservationData,
    STORED_EBIRD_DATA_KEY,
    STORED_EBIRD_DATA_VERSION
} from "../helpers/storedBirdData";
import {
    enrichEcoregionLocations,
    getUniqueEcoregionEnrichmentLocations
} from "../api/ecoregions";
import {
    enrichRegionLocations,
    getUniqueRegionEnrichmentLocations
} from "../api/regions";
import './data-onboarding.scss';

const EBIRD_DATA_FILE_ACCEPT = '.zip,application/zip,application/x-zip-compressed';

export type UploadDataProps = {
    onSetData: (data: any[], refreshedAt?: string) => void,
    onSetYears: React.Dispatch<any>
    onResetDataRequest: (e: React.MouseEvent) => void,
}

export default function UploadData({onSetData, onResetDataRequest, onSetYears}: UploadDataProps) {
    const [processingMessage, setProcessingMessage] = useState('');
    const [selectedDataFileName, setSelectedDataFileName] = useState('');
    const onboardingHeroImage = useMemo(() => getRandomDashboardHeroImage(), []);
    const isProcessing = Boolean(processingMessage);

    const isZipFile = (dataFile: File) => {
        return dataFile.name.toLowerCase().endsWith('.zip') ||
            ['application/zip', 'application/x-zip-compressed'].includes(dataFile.type);
    }

    const processDataFile = (dataFile: File) => {
        if (!isZipFile(dataFile)) {
            setSelectedDataFileName(dataFile.name);
            setProcessingMessage('');
            return;
        }

        setSelectedDataFileName(dataFile.name);

        const fr = new FileReader();
        setProcessingMessage('Reading your ZIP file and getting your sightings ready.');

        fr.addEventListener('load', async (e) => {
            try {
                const csvData = await loadDataFile(e.target?.result as ArrayBuffer);
                const jsonData = parseData(csvData);

                await processData(jsonData);
            } catch (error) {
                console.error(error);
                setProcessingMessage('');
            }
        });

        fr.addEventListener('error', () => {
            setProcessingMessage('');
        });

        fr.readAsArrayBuffer(dataFile);
    };

    const onFileChange = (e: ChangeEvent) => {
        const target = e.target as HTMLInputElement;
        const dataFile = target.files?.[0];
        if (!dataFile) return;

        processDataFile(dataFile);
    };

    const onDataFileDrop = (event: React.DragEvent<HTMLLabelElement>) => {
        event.preventDefault();

        const dataFile = event.dataTransfer.files?.[0];
        if (!dataFile || isProcessing) return;

        processDataFile(dataFile);
    };

    const processData = async (jsonData: any) => {
        const compactData = hydrateStoredObservationData(compactObservationData(jsonData));

        await clearDerivedBirdDataCaches();
        await enrichAndCacheEcoregionData(compactData);
        await enrichAndCacheRegionData(compactData);
        const refreshedAt = new Date().toISOString();
        await localforage.setItem(STORED_EBIRD_DATA_KEY, {
            version: STORED_EBIRD_DATA_VERSION,
            date: refreshedAt,
            signature: buildObservationDatasetSignature(compactData),
            data: compactData
        });
        onSetData(compactData, refreshedAt);
        onSetYears(getYearsFromStoredBirdData(compactData));
    }

    const enrichAndCacheEcoregionData = async (jsonData: any[]) => {
        const locations = getUniqueEcoregionEnrichmentLocations(jsonData);
        if (!locations.length) return;

        setProcessingMessage(`Adding biome and ecoregion context for ${locations.length.toLocaleString()} locations.`);

        try {
            const ecoDataByLocationId = await enrichEcoregionLocations(locations);

            await localforage.setItem(LOCATION_ECO_DATA_CACHE_KEY, {
                version: LOCATION_ECO_DATA_CACHE_VERSION,
                ecoDataByLocationId
            });
        } catch (error) {
            console.error(error);
        }
    }

    const enrichAndCacheRegionData = async (jsonData: any[]) => {
        const locations = getUniqueRegionEnrichmentLocations(jsonData);
        if (!locations.length) return;

        setProcessingMessage(`Adding region context for ${locations.length.toLocaleString()} locations.`);

        try {
            const regionDataByLocationId = await enrichRegionLocations(locations);

            await localforage.setItem(LOCATION_REGION_DATA_CACHE_KEY, {
                version: LOCATION_REGION_DATA_CACHE_VERSION,
                regionDataByLocationId
            });
        } catch (error) {
            console.error(error);
        }
    }

    return (
        <section className="data-onboarding" aria-labelledby="upload-title">
            <div className="data-onboarding__content">
                <h1 className="data-onboarding__headline" id="upload-title">
                    {isProcessing ? 'Processing your eBird data...' : 'Upload your eBird data file'}
                </h1>
                {isProcessing ?
                    <>
                        <div className="data-onboarding__loading-spinner data-onboarding__loading-spinner--centered" aria-hidden="true" />
                        <div className="data-onboarding__copy">
                            <p>{processingMessage} This may take a moment for larger exports.</p>
                        </div>
                    </> :
                    <>
                        <div className="data-onboarding__copy">
                            <p>Click below to select your eBird data ZIP file, or drag and drop the ZIP file here to load and process your eBird data.</p>
                        </div>
                        <label
                            className="data-onboarding__drop-zone"
                            htmlFor="dataFileUpload"
                            onDragOver={event => event.preventDefault()}
                            onDrop={onDataFileDrop}
                        >
                            <FontAwesomeIcon icon={faFileArrowUp} aria-hidden="true" />
                            <span>
                                <strong>{selectedDataFileName || 'Upload your eBird data file'}</strong>
                                <span>Drop or select your eBird ZIP export.</span>
                            </span>
                            <input type="file"
                                   name="dataFileUpload"
                                   id="dataFileUpload"
                                   placeholder="Upload Data File"
                                   accept={EBIRD_DATA_FILE_ACCEPT}
                                   disabled={isProcessing}
                                   onChange={onFileChange} />
                        </label>
                        <p className="data-onboarding__question">
                            <button type="button" className="data-onboarding__helper-link data-onboarding__helper-link--button" onClick={onResetDataRequest}>
                                <InfoIcon className="data-onboarding__helper-icon" />
                                Show me that download link again
                            </button>
                        </p>
                    </>
                }
                <div className="data-onboarding__privacy">
                    <InfoIcon className="data-onboarding__privacy-icon" />
                    <span>
                        <strong>Your data is private and secure.</strong>
                        <span>We never share your checklists with anyone.</span>
                    </span>
                </div>
            </div>
            <aside
                className="data-onboarding__hero"
                aria-label="Bird in a natural habitat"
                style={{'--data-onboarding-hero-image': `url("${onboardingHeroImage}")`} as React.CSSProperties}
            >
                <div className="data-onboarding__hero-card">
                    <div className="data-onboarding__feature">
                        <span className="data-onboarding__feature-marker" aria-hidden="true">1</span>
                        <span>
                            <strong className="data-onboarding__feature-title">Request your eBird Observations</strong>
                            <span className="data-onboarding__feature-copy">Request your personal eBird data export and check your email for a ZIP file.</span>
                        </span>
                    </div>
                    <div className="data-onboarding__feature">
                        <span className="data-onboarding__feature-marker" aria-hidden="true">2</span>
                        <span>
                            <strong className="data-onboarding__feature-title">Sync Your Sightings</strong>
                            <span className="data-onboarding__feature-copy">Drag or select the ZIP file here and BirdMap Explorer will read your eBird history. Your sightings stay on your computer.</span>
                        </span>
                    </div>
                    <div className="data-onboarding__feature">
                        <span className="data-onboarding__feature-marker" aria-hidden="true">3</span>
                        <span>
                            <strong className="data-onboarding__feature-title">Explore your world</strong>
                            <span className="data-onboarding__feature-copy">View your birding history as interactive maps, species / family checklists, and customized year views.</span>
                        </span>
                    </div>
                </div>
            </aside>
        </section>
    )
}
